import * as React from 'react';
import { Star } from 'lucide-react';

import { baseUrl } from '@workspace/routes';
import { Card, CardContent } from '@workspace/ui/components/card';
import { cn } from '@workspace/ui/lib/utils';

import { getTestimonialInitials, type Testimonial } from '../testimonials';

/** `avatar` is a marketing-app-relative path; resolve it to an absolute URL
 *  for consumers (e.g. the dashboard) that don't serve that public dir. */
export function resolveTestimonialAvatarSrc(avatar: string): string {
  return avatar.startsWith('http') ? avatar : `${baseUrl.Marketing}${avatar}`;
}

export type TestimonialCardLayout = 'center' | 'start';

export type TestimonialCardProps = {
  testimonial: Testimonial;
  /** `center` is the standalone card; `start` is the left-aligned wall card,
   *  which lets the quote run to its natural length so the masonry columns
   *  stagger instead of squaring off. */
  layout?: TestimonialCardLayout;
  className?: string;
};

function TestimonialAvatar({
  testimonial,
  className,
  textClassName
}: {
  testimonial: Testimonial;
  className: string;
  textClassName: string;
}): React.JSX.Element {
  if (testimonial.avatar) {
    return (
      <img
        src={resolveTestimonialAvatarSrc(testimonial.avatar)}
        alt={testimonial.name}
        className={cn('shrink-0 rounded-full object-cover', className)}
      />
    );
  }
  return (
    <div
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary',
        className,
        textClassName
      )}
    >
      {getTestimonialInitials(testimonial.name)}
    </div>
  );
}

function TestimonialStars({ rating }: { rating: number }): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-0.5"
      aria-label={`${rating} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'size-3.5',
            i < rating
              ? 'fill-amber-400 text-amber-400'
              : 'fill-muted text-muted-foreground/30'
          )}
        />
      ))}
    </div>
  );
}

/** Single review card — avatar/initials, name, role, quote, star rating. */
export function TestimonialCard({
  testimonial,
  layout = 'center',
  className
}: TestimonialCardProps): React.JSX.Element {
  if (layout === 'start') {
    return (
      <Card className={cn('gap-0 py-0', className)}>
        <CardContent className="flex flex-col gap-3 p-5">
          <div className="flex items-center gap-3">
            <TestimonialAvatar
              testimonial={testimonial}
              className="size-10"
              textClassName="text-sm"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {testimonial.name}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {testimonial.role}
              </p>
            </div>
          </div>
          <TestimonialStars rating={testimonial.rating} />
          {/* No line clamp: the varying heights are what make the wall stagger. */}
          <p className="text-sm leading-relaxed text-muted-foreground">
            “{testimonial.quote}”
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('h-full', className)}>
      <CardContent className="flex h-full flex-col items-center gap-3 p-6 text-center">
        <TestimonialAvatar
          testimonial={testimonial}
          className="size-14"
          textClassName="text-base"
        />
        <div>
          <p className="text-sm font-semibold text-foreground">
            {testimonial.name}
          </p>
          <p className="text-xs text-muted-foreground">{testimonial.role}</p>
        </div>
        <p className="line-clamp-5 flex-1 text-sm leading-relaxed text-muted-foreground">
          “{testimonial.quote}”
        </p>
        <TestimonialStars rating={testimonial.rating} />
      </CardContent>
    </Card>
  );
}
